import OwnershipStatement from './OwnershipStatement';
import {trpc} from '@/providers/trpc-client';
import Logo from '@/components/shared/Logo';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, ShieldCheck, FileCheck } from 'lucide-react';

export default function Footer() {
  const { t, i18n } = useTranslation('common');
  const {data:licenceData}=trpc.business.licence.useQuery();

  return (
    <footer className="bg-[#1A2332] text-white">
      {/* License Badge Banner */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <div className="flex items-center gap-3">
              <ShieldCheck size={22} className="text-[#C9A04C]" />
              <div>
                <p className="text-xs text-gray-400">{licenceData?.expiry.status==='expired'?(i18n.language.startsWith('ar')?'الرخصة قيد التجديد':'Licence under renewal'):t('footer.licensedIn')}</p>
                <p className="text-sm font-semibold text-white">{i18n.language.startsWith('ar')?licenceData?.profile.authorityAr:licenceData?.profile.authorityEn}</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-white/20" />
            <div className="flex items-center gap-3">
              <FileCheck size={22} className="text-[#C9A04C]" />
              <div>
                <p className="text-xs text-gray-400">{t('footer.licenseNumber')}</p>
                <p className="text-sm font-semibold text-white" dir="ltr">{licenceData?.profile.licence ?? ""}</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-white/20" />
            <div className="text-center sm:text-start">
              <p className="text-xs text-gray-400">{t('footer.companyName')}</p>
              <p className="text-sm font-semibold text-white">{licenceData?.profile.legalNameEn ?? ""}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <Logo theme="dark" size={26} /><p className="mt-2 text-xs text-gray-400" dir="ltr">{licenceData?.profile.licence ?? ""}</p>
            </div>
            <div className="text-sm text-gray-400 leading-relaxed mb-5">
              
            </div>
            {/* Social media links hidden - will be enabled when accounts are created */}
            {/*
            <div className="flex gap-2">
              {['FB', 'IG', 'LI'].map((social) => (
                <a
                  key={social}
                  href="#"
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-white/20 text-[10px] text-gray-400 hover:text-white hover:border-[#C9A04C] transition-all"
                >
                  {social}
                </a>
              ))}
            </div>
            */}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white mb-4">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-2.5">
              {[
                { label: t('footer.homeLink'), path: '/' },
                { label: t('footer.pricesLink'), path: '/visa-prices' },
                { label: t('footer.howLink'), path: '/how-to-apply' },
                { label: t('nav.guides'), path: '/guides' },
                { label: t('nav.news'), path: '/news' },
                { label: t('footer.trackLink'), path: '/track' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-gray-400 hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Center */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white mb-4">
              {t('footer.legalCenter')}
            </h3>
            <ul className="space-y-2.5">
              {[
                { label: i18n.language.startsWith('ar')?'التحقق من الرخصة':'Verify licence', path:'/verify' },
                { label: i18n.language.startsWith('ar')?'إخلاء المسؤولية':'Disclaimers', path:'/disclaimers' },
                { label: t('footer.terms'), path: '/terms' },
                { label: t('footer.privacy'), path: '/privacy' },
                { label: t('footer.refund'), path: '/refund' },
                { label: t('footer.cookies'), path: '/cookies' },
                { label: t('footer.contactLink'), path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-gray-400 hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Center */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white mb-4">
              {t('footer.supportCenter')}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2.5 text-sm text-gray-400">
                <Phone size={13} className="text-[#C9A04C] shrink-0" />
                <a dir="ltr" href="tel:+971502101784" className="hover:text-[#C9A04C] transition-colors">
                  +971 50 210 1784
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-gray-400">
                <Phone size={13} className="text-emerald-500 shrink-0" />
                <a dir="ltr" href="https://wa.me/971589896644" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A04C] transition-colors">
                  +971 58 989 6644 (WhatsApp)
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-gray-400">
                <Mail size={13} className="text-[#C9A04C] shrink-0" />
                <span>admin@tashiraev.com</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm text-gray-400">
                <MapPin size={13} className="text-[#C9A04C] shrink-0 mt-0.5" />
                <span>{t('footer.address')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-gray-300"><OwnershipStatement /></div>
          <p className="text-xs text-gray-500 tracking-widest uppercase">
            {t('footer.paymentMethods')}
          </p>
        </div>
      </div>
    </footer>
  );
}
